import GanttChartTabBar from "../components/ganttChart/GanttChartTabBar";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import VerticalGanttWrapper from "../components/ganttChart/VerticalGanttWrapper";
import TaskDrawerContent from "../components/taskDrawer/TaskDrawerContent";
import PondooPlanStatusBar from "../components/statusBar/PondooPlanStatusBar";
import ZoomComponent from "../components/ganttChart/ZoomComponent";
import { useEffect, useState } from "react";
import { Spinner } from "../components/UI/Spinner";
import KPIListOverview from "../components/kpi/KPIListOverview";
import MainContentWrapper from "../components/UI/pageStructure/MainContentWrapper";
import NexusExportErrorModal from "../components/header/NexusExportErrorModal";
import api, { API_PATHS } from "../service/api";
import { NexusSendResult, SendPlanToNexusBody } from "../types/apiTypes";
import { setNotification } from "../store/toastNotifications";
import { closeNexusErrorModal } from "../store/overlay";
import { clearSelectedEmployeeWorkTime, closeDrawer } from "../store/drawer";
import labels from "../utils/labels";
import Drawer from "../components/drawer/Drawer";
import DragAndDropStatusBar from "../components/ganttChart/dragAndDrop/DragAndDropStatusBar";
import statusBarsStyles from "../styles/statusBars.module.css";
import SessionWarningModal from "../components/ganttChart/dragAndDrop/SessionWarningModal";
import useDataSelector from "../hooks/useDataSelector";
import EmployeeDrawerContent from "../components/employeeDrawer/EmployeeDrawerContent";
import { useToggleGanttChartSpinner } from "../hooks/useToggleSpinner";
import { useSolutionPlanView } from "../hooks/useSolutionPlanView";
import { useFetchOnQueryChange } from "../hooks/useFetchOnQueryChange";
import GanttChartSearchBar from "../components/search/GanttChartSearchBar";
import UnsavedEmployeeEditsModal from "../components/employeeDrawer/UnsavedEmployeeEditsModal";
import DeleteWorkTimeModal from "../components/employeeDrawer/DeleteWorktimeModal";
import EmployeeSettingsService from "../service/employeeSettingsService";
import {
  clearEmployeeTransportEdit,
  editEmployeeWorkTime,
} from "../store/employeeSettings";
import LockAllToggle from "../components/ganttChart/verticalGanttChart/LockAllRoutesToggle";
import LockAllRoutesToggle from "../components/ganttChart/verticalGanttChart/LockAllRoutesToggle";

export default function GanttChartPage() {
  const dispatch = useAppDispatch();
  useToggleGanttChartSpinner(); //Handle showing spinner when state is loading.
  useFetchOnQueryChange();
  const dataToUse = useDataSelector();

  const { isDrawerOpen, drawerType, selectedEmployee, selectedTask } =
    useAppSelector((state) => state.drawerSlice);
  const { nexusErrorModal } = useAppSelector((state) => state.overlaySlice);
  const {
    ganttChartData,
    showSpinner,
    showSpinnerOnCustomData,
    error,
    selectedGanttChartTab,
  } = useAppSelector((state) => state.ganttChartSlice);
  const { isDragAndDropSessionWarningModalOpen } = useAppSelector(
    (state) => state.dragAndDropSlice,
  );
  const { kpiData } = useAppSelector((state) => state.kpiCollectionSlice);
  const [hasEmployeeUnsavedEdits, setHasEmployeeUnsavedEdits] = useState(false);
  const [isUnsavedEditsModalOpen, setIsUnsavedEditsModalOpen] = useState(false);
  const [isDeleteWorkTimeModalOpen, setIsDeleteWorkTimeModalOpen] =
    useState(false);

  const planViewState = useSolutionPlanView();

  useEffect(() => {
    const ganttChartElement = document.getElementById("outer-scroll-wrapper");
    if (selectedGanttChartTab === "Custom" && ganttChartElement)
      ganttChartElement.style.userSelect = "none";
    else if (ganttChartElement) ganttChartElement.style.userSelect = "";
  }, [selectedGanttChartTab]);

  const showOriginalDashbordData =
    selectedGanttChartTab === "Original" && kpiData?.nexusDashboard;
  const showPondooDashboardData =
    selectedGanttChartTab === "Optimised" && kpiData?.pondooDashboard;

  const getContinuityScore = () => {
    if (showOriginalDashbordData)
      return kpiData?.nexusDashboard.solutionKpi.continuity;
    else if (showPondooDashboardData)
      return kpiData?.pondooDashboard.solutionKpi.continuity;
    else return 0;
  };

  const getDistance = () => {
    if (showOriginalDashbordData)
      return kpiData?.nexusDashboard.otherKpi.drivenKilometers;
    else if (showPondooDashboardData)
      return kpiData?.pondooDashboard.otherKpi.drivenKilometers;
    else return undefined;
  };

  /**
   * Handle sending to Nexus ignoring any misalignments between the Pondoo plan and the state of Nexus
   */
  const forceSendToNexus = async () => {
    if (!ganttChartData?.idScenario) return;

    try {
      const body: SendPlanToNexusBody = {
        idScenario: ganttChartData.idScenario,
        forceSend: true,
      };
      const response = await api.post<NexusSendResult>(
        API_PATHS.postNexusPlan,
        body,
      );

      if (response.status === 200 && response.data?.success) {
        dispatch(closeNexusErrorModal());
        dispatch(
          setNotification({
            message: labels.nexusExportSuccess,
            type: "send_to_nexus",
            variant: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: response.data?.message || labels.nexusExportErrorGeneral,
            type: "send_to_nexus",
          }),
        );
      }
    } catch (error) {
      dispatch(
        setNotification({
          message: labels.nexusExportErrorGeneral,
          type: "send_to_nexus",
        }),
      );
    }
  };

  async function handleConfirmDeleteWorkTime() {
    if (!selectedEmployee) return;
    const shiftId = selectedEmployee.employeeShiftId;
    try {
      await EmployeeSettingsService.deleteEmployeeWorkTime(shiftId, dispatch);
      dispatch(clearSelectedEmployeeWorkTime());
      dispatch(clearEmployeeTransportEdit({ shiftId }));
      dispatch(
        editEmployeeWorkTime({
          shiftId,
          workTimeStart: null,
          workTimeEnd: null,
        }),
      );
      setIsDeleteWorkTimeModalOpen(false);
    } catch {}
  }

  return (
    <>
      <section className="flex flex-col relative">
        <div className="flex justify-end min-h-[43px]">
          <GanttChartTabBar />
          {ganttChartData && planViewState.status === "ready" && (
            <GanttChartSearchBar
              employees={ganttChartData.employees}
              unplannedTasks={
                ganttChartData.generalGanttChartInfo.unplannedTasks.workTasks
              }
            />
          )}
        </div>

        {/* TODO: figure out how to take the first element in the array passed to the tabs bar */}
        <MainContentWrapper
          classes="flex flex-col"
          firstTab="Original"
          selectedTab={selectedGanttChartTab}
        >
          {
            <article className="w-full flex justify-center items-center p-3 min-h-[54px] gap-[--spacing-xl]">
{/*               <div style={{ position: "absolute", left: "var(--padding-xl)" }}>
                <LockAllRoutesToggle />
              </div> */}
              {(showOriginalDashbordData || showPondooDashboardData) && (
                <KPIListOverview
                  continuity={getContinuityScore()}
                  distance={getDistance()}
                />
              )}
            </article>
          }
          {["missing", "error"].includes(planViewState.status) &&
            !showSpinner &&
            selectedGanttChartTab !== "Custom" && (
              <div className="flex flex-col gap-[10px] items-center justify-center h-full">
                {planViewState.status === "error" ? (
                  <p style={{ color: "var(--col-error-3)" }}>
                    {planViewState.errorMessage ?? error}
                  </p>
                ) : (
                  <p>
                    <b>Vi mangler en plan for i dag.</b> <br />
                    Gå til Nexus for at sende data fra kalenderen.
                  </p>
                )}
              </div>
            )}

          {showSpinner && <Spinner fullSize />}
          {showSpinnerOnCustomData && <Spinner fullSize />}
          {((selectedGanttChartTab !== "Custom" &&
            dataToUse &&
            planViewState.status === "ready") ||
            (selectedGanttChartTab === "Custom" && dataToUse)) &&
            !showSpinner && <VerticalGanttWrapper />}
        </MainContentWrapper>
      </section>

      {isDrawerOpen && drawerType === "Task" && selectedTask && (
        <Drawer
          onClose={() => dispatch(closeDrawer())}
          heading={`${labels.visit}: ${selectedTask.task.client.description}`}
        >
          <TaskDrawerContent />
        </Drawer>
      )}
      {isDrawerOpen && drawerType === "Employee" && selectedEmployee && (
        <Drawer
          onClose={() => {
            hasEmployeeUnsavedEdits
              ? setIsUnsavedEditsModalOpen(true)
              : dispatch(closeDrawer());
          }}
          heading={selectedEmployee.employeeName || ""}
        >
          <EmployeeDrawerContent
            onEmployeeUnsavedEdit={setHasEmployeeUnsavedEdits}
            onDeleteWorkTimeRequest={() => {
              setIsDeleteWorkTimeModalOpen(true);
            }}
          />
        </Drawer>
      )}
      <div className={statusBarsStyles.statusBarContainer}>
        {selectedGanttChartTab === "Custom" && <DragAndDropStatusBar />}
        <PondooPlanStatusBar />
      </div>
      {((selectedGanttChartTab !== "Custom" &&
        dataToUse &&
        planViewState.status === "ready") ||
        (selectedGanttChartTab === "Custom" && dataToUse)) &&
        !showSpinner && <ZoomComponent />}

      {/* Error modal shown when sending to Nexus in the event of failure */}
      {nexusErrorModal && (
        <NexusExportErrorModal
          isOpen={!!nexusErrorModal}
          failedTasks={nexusErrorModal.failedTasks}
          message={nexusErrorModal.message}
          onForceSend={forceSendToNexus}
        />
      )}
      {/* Warning modal when user closes the edit tab without saving first. */}
      {isDragAndDropSessionWarningModalOpen && <SessionWarningModal />}
      {/* Warning modal when user closes the Employee drawer without saving first */}
      {isUnsavedEditsModalOpen && (
        <UnsavedEmployeeEditsModal
          onKeepEditing={() => {
            setIsUnsavedEditsModalOpen(false);
          }}
          onDiscard={() => {
            setIsUnsavedEditsModalOpen(false);
            setHasEmployeeUnsavedEdits(false);
            dispatch(closeDrawer());
          }}
        />
      )}
      {isDeleteWorkTimeModalOpen && (
        <DeleteWorkTimeModal
          onDelete={handleConfirmDeleteWorkTime}
          onDiscard={() => setIsDeleteWorkTimeModalOpen(false)}
        />
      )}
    </>
  );
}
